import {
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Theme,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { Search as SearchIcon } from "lucide-react";
import { ReactNode } from "react";

export interface PageLayoutSearchProps {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
}

export interface PageLayoutProps {
  title: ReactNode;
  subtitle?: ReactNode;
  /** Search field in header (SWC style: right-aligned, placeholder, icon) */
  search?: PageLayoutSearchProps;
  actions?: ReactNode;
  children: ReactNode;
}

const PageLayout = ({
  title,
  subtitle,
  search,
  actions,
  children,
}: PageLayoutProps) => {
  const isMobile = useMediaQuery<Theme>((t) => t.breakpoints.down("sm"));

  return (
    <Stack
      direction="column"
      spacing={{ xs: 1.5, sm: 2.5 }}
      overflow="hidden"
      flex={1}
    >
      <Paper sx={{ p: { xs: 1.5, sm: 3 }, borderRadius: 2 }}>
        <Stack spacing={isMobile ? 1.5 : 0}>
          {/* Title row */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={2}
          >
            <Stack>
              <Typography
                variant={isMobile ? "headingSM" : "headingLG"}
                fontWeight={500}
              >
                {title}
              </Typography>
              {subtitle != null && (
                <Typography
                  variant={isMobile ? "textXS" : "labelMD"}
                  sx={{
                    mt: 0.25,
                    color: (t: any) =>
                      t.palette.brandText?.secondary ?? "#818181",
                  }}
                >
                  {subtitle}
                </Typography>
              )}
            </Stack>
            {!isMobile && actions != null && <Stack>{actions}</Stack>}
          </Stack>

          {/* Search - own row on mobile */}
          {search != null && (
            <TextField
              size="small"
              placeholder={search.placeholder ?? "Search..."}
              value={search.value}
              onChange={search.onChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon size={20} color="#B8BDBD" />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: { xs: "100%", sm: 350 },
                alignSelf: { sm: "flex-end" },
                "& .MuiInputBase-root": {
                  borderRadius: 10,
                  py: 0.5,
                },
              }}
            />
          )}

          {/* Actions - own row on mobile */}
          {isMobile && actions != null && (
            <Stack direction="row" sx={{ width: "100%" }}>
              {actions}
            </Stack>
          )}
        </Stack>
      </Paper>
      <Paper
        sx={{
          p: { xs: 1, sm: 3 },
          borderRadius: 2,
          overflow: "hidden",
          flex: 1,
        }}
      >
        {children}
      </Paper>
    </Stack>
  );
};

export default PageLayout;
